import { useRouter } from "next/router";
import { useCallback } from "react";

type RedirectTarget =
  | string
  | {
      pathname: string;
      query?: Record<string, string | string[] | undefined>;
    };

type RedirectOptions = {
  shallow?: boolean;
  replace?: boolean;
  as?: string;
  locale?: string;
  asWindow?: boolean;
  asIframe?: boolean;
  asNewTab?: boolean;
};

/**
 * Single abstraction for all programmatic in-app navigation.
 *
 * Use `useRedirect` for navigating the user to another page.
 * Use `useRouter()` only for reading router state (query, pathname, etc.).
 * Use `<Link>` for declarative navigation (clickable links).
 */
export default function useRedirect() {
  const router = useRouter();
  const redirect = useCallback(
    (to: RedirectTarget, opts?: RedirectOptions) => {
      if (typeof window === "undefined") {
        return;
      }

      if (typeof to === "string") {
        if (opts?.asIframe && window.top) {
          window.top.location.href = to;
          return;
        }

        if (opts?.asWindow) {
          window.location.href = to;
          return;
        }

        if (opts?.asNewTab) {
          window.open(to, "_blank");
          return;
        }
      }

      if (router.isReady) {
        const method = opts?.replace ? router.replace : router.push;
        const nextOptions: { shallow?: boolean; locale?: string } = {};
        if (opts?.shallow) nextOptions.shallow = true;
        if (opts?.locale) nextOptions.locale = opts.locale;
        method(to, opts?.as, nextOptions);
      }
    },
    [router]
  );

  return { redirect };
}
